import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataGridTypes } from 'devextreme-react/data-grid';
import { Button, CheckBox } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import FilterPopup from './FilterPopup';
import FilterParameterDto from '../../classes/dtos/datasourcemodule/FilterParameterDto';
import FilterParameterVariableDto from '../../classes/dtos/datasourcemodule/FilterParameterVariableDto';
import DataTableFieldParameterDefinitionDto from '../../classes/dtos/datasourcemodule/DataTableFieldParameterDefinitionDto';
import { describeRowFilters, describeDeclaredVariables, rowFilterValue } from '../../logics/datasourcemodule/RowFilterLogic';
import TableApi from '../../api/TableApi';

interface UseFilterColumnOptions {
    title: string;

    // Field on the row data used as the row's stable identity (e.g. 'SourceFieldReferenceId', or
    // Synchro's 'TargetFieldReferenceId'). See getEffectiveFilters for why this is needed.
    rowKeyField: string;

    // Source table the filtering capabilities (allowed operators / field type) are read from.
    tableReferenceId: number;

    // Row field the (hidden, batch-editable) simple-mode filter conditions are stored under. Defaults to 'Filters'.
    filtersField?: string;

    // Row field the (hidden, batch-editable) complex-mode declared variables are stored under
    // (DataTableFieldParameterDefinitionDto[]). Defaults to 'DeclaredVariables'.
    declaredVariablesField?: string;

    // Field the filters apply to — carried on the built FilterParameter*Dto.
    getRowFieldReferenceId: (rowData: any) => number;

    // Type (FieldType) of the ORIGIN column, before transformation — drives the constant editor.
    getRowOriginFieldType: (rowData: any) => number;

    // Prefill value for a "variable" filter's technical name — like the MetaApi TechnicalName column.
    getPrefillTechnicalName: (rowData: any) => string;

    // Optional: hide the button entirely for a row. The exposed-field grids hide it on unselected /
    // unmanaged rows purely in CSS (`.hide-btn-when-unselected`), so they don't pass this; Synchro
    // has no selection column and passes it to hide the button on an unmapped row (no source field).
    isRowActive?: (rowData: any) => boolean;

    // Optional: given the full grid cell, returns the row data `isRowActive` should read, with pending
    // batch edits from other columns folded in (e.g. Synchro's live source-field pick, read from
    // `cellData.component.cellValue()`). Without it `isRowActive` only sees the persisted row and the
    // button won't appear/disappear as fields are linked/unlinked before a save. Defaults to `cellData.data`.
    resolveRowData?: (cellData: any) => any;

    // "Advanced mode" checkbox shown in the FILTER column header. Page-level state persisted with the
    // path (mapping version). In advanced mode the popup only NAMES filter variables for the row; the
    // operators / grouping are then authored in the Filters tab. In simple mode the popup is the full
    // constant-or-variable editor.
    advancedMode?: boolean;
    onAdvancedModeChange?: (value: boolean) => void;

    // Advanced mode is a one-way switch: once it has been saved, it can't be turned back off. When
    // locked the header checkbox is disabled (and stays checked).
    advancedModeLocked?: boolean;
}

interface UseFilterColumnResult {
    ButtonCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    HeaderCell: () => JSX.Element;
    PopupElement: JSX.Element;
    calculateDisplayValue: (rowData: any) => string;
}

// operators bit-mask + field type keyed by FieldReferenceId, from the source's filtering capabilities.
interface FieldCapability {
    operators: number;
    type: number;
}

/**
 * Shared logic behind the editable "FILTER" button + popup used across the Synchro, Reporting,
 * MetaApi, DataTable and AI mapping grids. The column is always shown; `advancedMode` (the header
 * checkbox) switches the popup between the full constant-or-variable editor (simple) and a plain
 * list of variable names whose expression is authored in the Filters tab (complex).
 *
 * Like the transformation column, the grid's own batch-edit state is the single source of truth:
 * the popup writes the new filter list with `cellValue(rowIndex, filtersField, ...)`, which the
 * button then re-reads (`component.cellValue`) so pending edits show without a save.
 */
function useFilterColumn(options: UseFilterColumnOptions): UseFilterColumnResult {
    const { title, rowKeyField, tableReferenceId, getRowFieldReferenceId, getRowOriginFieldType, getPrefillTechnicalName, isRowActive, resolveRowData, advancedMode, onAdvancedModeChange, advancedModeLocked } = options;
    const filtersField = options.filtersField ?? 'Filters';
    const declaredVariablesField = options.declaredVariablesField ?? 'DeclaredVariables';
    const isAdvanced = advancedMode ?? false;

    // Read the visibility predicates through refs: ButtonCell is memoised (so opening / closing the
    // popup doesn't repaint every FILTER cell), and devextreme-react can run an older ButtonCell
    // closure after a repaint — both must always see the latest render's functions.
    const isRowActiveRef = useRef(isRowActive);
    isRowActiveRef.current = isRowActive;
    const resolveRowDataRef = useRef(resolveRowData);
    resolveRowDataRef.current = resolveRowData;

    const [popup, setPopup] = useState<{ cellData: DataGridTypes.ColumnCellTemplateData } | null>(null);
    const [capabilitiesByField, setCapabilitiesByField] = useState<Map<number, FieldCapability>>(new Map());

    useEffect(() => {
        if (!tableReferenceId || tableReferenceId <= 0) {
            setCapabilitiesByField(new Map());
            return;
        }
        let cancelled = false;
        TableApi.GetFilteringCapabilitiesAsync(tableReferenceId).then((result) => {
            if (cancelled || result.IsFailed()) {
                return;
            }
            const map = new Map<number, FieldCapability>();
            result.Result.FilteringCapabilities.forEach((e) => map.set(e.Id, { operators: e.Operators, type: e.Type }));
            setCapabilitiesByField(map);
        });
        return () => { cancelled = true; };
    }, [tableReferenceId]);

    // Current filter list for a row. Given a grid cell arg it reads the live batch value
    // (`component.cellValue`), which includes unsaved edits; given a plain row it returns the
    // persisted field (sort / filter paths, which have no grid handle).
    const getEffectiveFilters = useCallback((arg: any): FilterParameterDto[] => {
        const component = arg?.component;
        const rowIndex = arg?.rowIndex ?? arg?.row?.rowIndex;
        const rowData = arg?.data ?? arg;
        const hasGridHandle =
            component && typeof component.cellValue === 'function' &&
            rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 &&
            // During virtual scrolling DevExtreme can call cellRender for a recycled row before its
            // internal cellValue cache has caught up with the row now at this index; cellValue would
            // then briefly answer with another row's filters, flashing the button red/green until the
            // next repaint resyncs them. Guard against it by checking the row key still matches
            // (mirrors the same guard in useTransformationColumn).
            component.cellValue(rowIndex, rowKeyField) === rowData?.[rowKeyField];

        if (hasGridHandle) {
            const live = component.cellValue(rowIndex, filtersField);
            // DevExtreme's batch `cellValue` getter doesn't always round-trip a non-primitive
            // (array) cell value — fall back to the row object, which `onApply` keeps in sync.
            if (Array.isArray(live)) {
                return live as FilterParameterDto[];
            }
            return (rowData?.[filtersField] as FilterParameterDto[]) ?? [];
        }

        return (rowData?.[filtersField] as FilterParameterDto[]) ?? [];
    }, [filtersField, rowKeyField]);

    // Same read strategy as getEffectiveFilters, for the row's complex-mode declared variables.
    const getEffectiveDeclaredVariables = useCallback((arg: any): DataTableFieldParameterDefinitionDto[] => {
        const component = arg?.component;
        const rowIndex = arg?.rowIndex ?? arg?.row?.rowIndex;
        const rowData = arg?.data ?? arg;
        const hasGridHandle =
            component && typeof component.cellValue === 'function' &&
            rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 &&
            // Same recycled-row guard as getEffectiveFilters above.
            component.cellValue(rowIndex, rowKeyField) === rowData?.[rowKeyField];

        if (hasGridHandle) {
            const live = component.cellValue(rowIndex, declaredVariablesField);
            if (Array.isArray(live)) {
                return live as DataTableFieldParameterDefinitionDto[];
            }
            return (rowData?.[declaredVariablesField] as DataTableFieldParameterDefinitionDto[]) ?? [];
        }

        return (rowData?.[declaredVariablesField] as DataTableFieldParameterDefinitionDto[]) ?? [];
    }, [declaredVariablesField, rowKeyField]);

    // Variable technical names used by every OTHER row — the popup keeps the edited row's name unique.
    // Gathered from both the simple-mode conditions and the complex-mode declared variables.
    const getTakenTechnicalNames = (cellData: DataGridTypes.ColumnCellTemplateData): string[] => {
        const component = cellData?.component;
        const currentRowIndex = cellData?.row?.rowIndex;
        if (!component || typeof component.getVisibleRows !== 'function') {
            return [];
        }
        const names: string[] = [];
        component.getVisibleRows().forEach((row: any) => {
            if (row.rowType !== 'data' || row.rowIndex === currentRowIndex) {
                return;
            }
            const filters = (component.cellValue(row.rowIndex, filtersField) as FilterParameterDto[]) ?? [];
            filters.forEach((f) => {
                if (f instanceof FilterParameterVariableDto && f.TechnicalName.trim().length > 0) {
                    names.push(f.TechnicalName);
                }
            });
            const declaredVariables = (component.cellValue(row.rowIndex, declaredVariablesField) as DataTableFieldParameterDefinitionDto[]) ?? [];
            declaredVariables.forEach((d) => {
                if (d.TechnicalName.trim().length > 0) {
                    names.push(d.TechnicalName);
                }
            });
        });
        return names;
    };

    // <prefill>, then <prefill>_2, _3… until it no longer collides with another row's variable name.
    const getUniquePrefill = (base: string, taken: string[]): string => {
        const takenLower = new Set(taken.map((n) => n.trim().toLowerCase()));
        if (!takenLower.has(base.trim().toLowerCase())) {
            return base;
        }
        for (let i = 2; i < 100; i++) {
            const candidate = `${base}_${i}`;
            if (!takenLower.has(candidate.toLowerCase())) {
                return candidate;
            }
        }
        return base;
    };

    const calculateDisplayValue = useCallback((rowData: any): string =>
        isAdvanced
            ? describeDeclaredVariables((rowData?.[declaredVariablesField] as DataTableFieldParameterDefinitionDto[]) ?? [])
            : describeRowFilters((rowData?.[filtersField] as FilterParameterDto[]) ?? []),
        [isAdvanced, filtersField, declaredVariablesField]);

    // A configured filter is "complete" when every entry has a right-hand side — a non-empty constant
    // Value or variable TechnicalName. Mirrors the transformation button's isComplete().
    const isFilterComplete = useCallback((filters: FilterParameterDto[]): boolean =>
        filters.length > 0 && filters.every((f) => rowFilterValue(f).trim().length > 0), []);

    // Memoised so opening / closing the popup (a `setPopup` in this hook) doesn't change the
    // `cellRender` identity and force DevExtreme to repaint every FILTER cell in the grid.
    const ButtonCell = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
        const rowData = resolveRowDataRef.current ? resolveRowDataRef.current(cellData) : cellData?.data;

        if (isRowActiveRef.current && !isRowActiveRef.current(rowData)) {
            return <></>;
        }

        const filters = isAdvanced ? [] : getEffectiveFilters(cellData);
        const declaredVariables = isAdvanced ? getEffectiveDeclaredVariables(cellData) : [];

        const summary = isAdvanced ? describeDeclaredVariables(declaredVariables) : describeRowFilters(filters);
        const configured = isAdvanced ? declaredVariables.length > 0 : filters.length > 0;

        // Same colour rule as the transformation button: neutral outlined while empty, then a filled
        // button once configured — green when every entry has a value, red while one is still missing
        // (e.g. a constant filter applied with an empty value).
        const stylingMode: 'contained' | 'outlined' = configured ? 'contained' : 'outlined';
        const complete = isAdvanced
            ? declaredVariables.every((d) => d.TechnicalName.trim().length > 0)
            : isFilterComplete(filters);
        const type: 'normal' | 'success' | 'danger' =
            !configured ? 'normal' : complete ? 'success' : 'danger';

        const emptyText = isAdvanced ? formatMessage('FilterColumn_NoVariable') : formatMessage('FilterColumn_None');

        return (
            <Button
                text={configured ? (summary || formatMessage('FilterColumn_View')) : emptyText}
                // Complex mode only NAMES filter variables (the expression lives in the Filters tab),
                // so mark those cells with a distinct "variable" glyph instead of the funnel.
                icon={isAdvanced ? 'variable' : 'filter'}
                stylingMode={stylingMode}
                type={type}
                width="100%"
                elementAttr={{ class: 'filter-button-cell' }}
                onClick={(e) => {
                    e.event?.stopPropagation();
                    setPopup({ cellData });
                }}
            />
        );
    }, [getEffectiveFilters, getEffectiveDeclaredVariables, isFilterComplete, isAdvanced]);

    // Sub-header of the FILTER band column: the "Advanced mode" checkbox, sitting on the sub-header row
    // right under the "FILTER" band caption (same two-row structure as the SOURCE / TARGET bands).
    // stopPropagation keeps a click on the checkbox from also triggering the column's sort (same trick
    // as the Synchro grid's custom header buttons).
    // A disabled DevExtreme widget gets `pointer-events: none`, so its own `hint` (a native title
    // tooltip) never shows on hover — carry the explanation on the wrapper instead.
    const lockedHint = advancedModeLocked ? formatMessage('FilterColumn_AdvancedModeLocked') : undefined;

    const HeaderCell = useCallback(() => (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }} title={lockedHint}>
            <CheckBox
                text={formatMessage('FilterColumn_AdvancedMode')}
                value={advancedMode ?? false}
                disabled={advancedModeLocked ?? false}
                onValueChanged={(e) => {
                    // e.event is only set for genuine user interaction; ignore programmatic value syncs, which
                    // would otherwise push a stale `false` back into the page state and hide the Filters tab again.
                    if (!e.event) {
                        return;
                    }
                    e.event.stopPropagation();
                    if (advancedModeLocked && !e.value) {
                        return;
                    }
                    onAdvancedModeChange?.(!!e.value);
                }}
            />
        </div>
    ), [advancedMode, advancedModeLocked, onAdvancedModeChange, lockedHint]);

    const takenTechnicalNames = popup ? getTakenTechnicalNames(popup.cellData) : [];
    const fieldReferenceId = popup ? getRowFieldReferenceId(popup.cellData.data) : 0;
    const capability = capabilitiesByField.get(fieldReferenceId);
    // Prefer the row's own origin type; fall back to the type known to the filtering capabilities.
    const originFieldType = (popup ? getRowOriginFieldType(popup.cellData.data) : 0) || capability?.type || 0;

    // Stage a batch change on the edited row (feeds the page's mapping state + the save) and force a
    // repaint — the button's calculated summary may be unchanged, so DevExtreme won't repaint on its
    // own. Also keep the row object authoritative: the batch `cellValue` getter doesn't reliably
    // round-trip an array cell value.
    const writeRowValue = (field: string, value: unknown): void => {
        if (!popup) {
            return;
        }
        const dataGrid = popup.cellData.component;
        const rowIndex = popup.cellData.rowIndex ?? popup.cellData.row?.rowIndex;
        if (dataGrid && typeof rowIndex === 'number' && rowIndex >= 0) {
            dataGrid.cellValue(rowIndex, field, value);
            dataGrid.repaintRows([rowIndex]);
        }
        if (popup.cellData.data) {
            popup.cellData.data[field] = value;
        }
    };

    const PopupElement = (
        <FilterPopup
            visible={popup !== null}
            title={title}
            currentFilters={popup ? getEffectiveFilters(popup.cellData) : []}
            currentDeclaredVariableNames={popup ? getEffectiveDeclaredVariables(popup.cellData).map((d) => d.TechnicalName) : []}
            fieldReferenceId={fieldReferenceId}
            originFieldType={originFieldType}
            operators={capability?.operators ?? 0}
            prefillTechnicalName={popup ? getUniquePrefill(getPrefillTechnicalName(popup.cellData.data), takenTechnicalNames) : ''}
            takenTechnicalNames={takenTechnicalNames}
            advancedMode={advancedMode ?? false}
            onApply={(filters) => {
                writeRowValue(filtersField, filters);
                setPopup(null);
            }}
            onApplyDeclaredVariableNames={(names) => {
                writeRowValue(declaredVariablesField, names.map((name) => new DataTableFieldParameterDefinitionDto(0, name)));
                setPopup(null);
            }}
            onCancel={() => setPopup(null)}
        />
    );

    return { ButtonCell, HeaderCell, PopupElement, calculateDisplayValue };
}

export default useFilterColumn;
